import * as React from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "../../../components/ui/button";
import { InstagramIcon } from "../../../assets/svgs/common/instagram.svg";
import { DataTableComponents } from "../../../components/tables/table.components";
import Spinner from "../../../components/spinner.components";
import { useNavigate } from "react-router-dom";
import {
  SocialPlatforms,
  SocialApplicationStatus,
  type ISocial,
} from "../../../types/models/social.models.types";
import { useGetSocialsAdminHttp } from "../../../hooks/http/admin/socials/get-socials.admin.http";
import { ApproveButton } from "../../../components/action-buttons/socials/approve-button.components";
import useUserStore from "../../../store/socials.store";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { useToast } from "../../../hooks/use-toast";
import millify from "millify";
import { handleHttpError } from "../../../utils/utils";
import { RejectButton } from "../../../components/action-buttons/socials/reject-button.component";
import GrowthIcon from "../../../assets/svgs/common/growth.svg";
import { AddToGroup } from "../../../components/action-buttons/favorites/add-to-group-button.components";
import { DateTime } from "luxon";
import { Youtube } from "lucide-react";

type IProps = {
  applicationStatus: `${SocialApplicationStatus}` | "search";
  tableType?: string;
  searchFetching?: boolean;
};

export const SocialTableComponents: React.FunctionComponent<IProps> = ({
  applicationStatus,
  tableType,
  searchFetching,
}) => {
  const [reset, setReset] = React.useState(true);
  const [sortBy, setSortBy] = React.useState("createdAt");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { data, setData, removeById } = useUserStore();
  const navigate = useNavigate();
  const [pageNo, setPageNo] = React.useState(1);
  const [paginationData, setPaginationData] = React.useState<IPagination>();
  const {
    execute: getSocialsHttp,
    fetching,
    cancel: cancelGetSocialsHttp,
  } = useGetSocialsAdminHttp();
  const { toast } = useToast();

  async function getSocials(existingSocials: ISocial[]) {
    cancelGetSocialsHttp();
    const response = await getSocialsHttp({
      body: null,
      queryParams: {
        page: pageNo,
        applicationStatus: applicationStatus,
        sortBy: sortBy,
        size: 50,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    const newSocials = response.data.socials.filter((social) => {
      return existingSocials.findIndex((i) => i.id === social.id) === -1;
    });

    setData([...existingSocials, ...newSocials]);
    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    if (tableType === "search" || tableType === "searchFavorites") {
      return;
    }
    if (reset) {
      setReset(false);
      setData([]);
      getSocials([]);
    } else {
      getSocials(data);
    }
  }, [pageNo, sortBy]);

  function handleViewDetails(id: string) {
    navigate(`/admin/socials/${id}`);
  }

  const RenderActionButtons = (social: ISocial) => {
    if (tableType === "searchFavorites") {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
          <AddToGroup social={social} removeById={removeById} />
        </div>
      );
    }

    if (
      tableType === "search"
        ? social.applicationStatus.status === SocialApplicationStatus.applied
        : applicationStatus === SocialApplicationStatus.applied
    ) {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
          <ApproveButton social={social} removeById={removeById} />

          <RejectButton social={social} />
        </div>
      );
    }
    if (
      tableType === "search"
        ? social.applicationStatus.status === SocialApplicationStatus.approved
        : applicationStatus === SocialApplicationStatus.approved
    ) {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
          <RejectButton social={social} />
        </div>
      );
    }
    if (
      tableType === "search"
        ? social.applicationStatus.status === SocialApplicationStatus.rejected
        : applicationStatus === SocialApplicationStatus.rejected
    ) {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
          <ApproveButton social={social} removeById={removeById} />
        </div>
      );
    }
    if (
      tableType === "search"
        ? social.applicationStatus.status === SocialApplicationStatus.external
        : applicationStatus === SocialApplicationStatus.external
    ) {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
        </div>
      );
    }
    if (
      tableType === "search"
        ? social.applicationStatus.status === SocialApplicationStatus.deleted
        : applicationStatus === SocialApplicationStatus.deleted
    ) {
      return (
        <div className="flex justify-end items-center gap-4">
          <div className="text-rejectbuttontext">
            Deleted
          </div>
          <Button
            onClick={() => {
              handleViewDetails(social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
        </div>
      );
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const columns = React.useMemo<ColumnDef<ISocial>[]>(
    () => [
      {
        accessorKey: "handle",
        header: "User name",
        cell: ({ row }) => {
          const { handle, platform, youtube } = row.original;

          // Decide what to show based on platform field
          if (platform === SocialPlatforms.youtube) {
            const channelHandle = youtube?.channelHandle || handle;
            return (
              <div className="flex items-center gap-3 font-semibold tracking-wider">
                <div className="flex justify-center items-center border-2 rounded-full w-8 h-8 overflow-hidden">
                  <img
                    className="rounded-full w-8 h-8 cover"
                    src={youtube?.profileImage}
                    alt=""
                  />
                </div>
                <a
                  href={`https://youtube.com/${channelHandle}/`}
                  target="_blank"
                  className="underline"
                  aria-label="YouTube channel link"
                  rel="noreferrer"
                >
                  {channelHandle}
                </a>
              </div>
            );
          }

          // Default: instagram
          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <div className="border-2 rounded-full w-8 h-8" />
              <a
                href={`https://instagram.com/${handle}/`}
                target="_blank"
                className="underline"
                aria-label="Instagram profile link"
                rel="noreferrer"
              >
                {handle}
              </a>
            </div>
          );
        },
      },
      {
        accessorKey: "followersCount",
        header: "Followers & engagement",
        cell: ({ row }) => {
          const { engagementRate, platform, youtube } = row.original;

          // YouTube: subscribers & engagement, different label/icon if you want
          if (platform === SocialPlatforms.youtube) {
            return (
              <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
                <Youtube />
                <div className="flex justify-start items-center gap-2 w-10">
                  {millify(youtube?.subscriberCount ?? 0)}
                </div>
                <div className="border-border border-l h-5" />
                <div className="flex justify-start items-center gap-2 w-fit">
                  <GrowthIcon />
                  {youtube?.metrics?.engagementRate?.toFixed() || "-"}%
                </div>
              </div>
            );
          }

          // Default: Instagram followers & engagement
          return (
            <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
              <InstagramIcon className="w-5 h-5" />
              <div className="flex justify-start items-center gap-2 w-10">
                {millify(row.original.followersCount || 0)}
              </div>
              <div className="border-border border-l h-5" />
              <div className="flex justify-start items-center gap-2 w-fit">
                <GrowthIcon />
                {engagementRate?.toFixed()}%
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "creatorType",
        header: "Type of creator",
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 font-semibold capitalize tracking-wider">
              {row.getValue("creatorType")}
            </div>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Onboarding time",
        cell: ({ row }) => {
          const { createdAt } = row.original;

          return (
            <div className="flex items-center gap-2 font-semibold text-secondary tracking-wider">
              {DateTime.fromISO(createdAt?.toString() || "")
                .setZone("local")
                .toFormat("hh:mm a'\n'dd-MM-yy")}
            </div>
          );
        },
      },
      {
        accessorKey: "id",
        header: "",
        cell: ({ row: social }) => RenderActionButtons(social.original),
      },
    ],
    [],
  );
  const table = useReactTable({
    data,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  if ((data.length <= 0 && fetching) || searchFetching) {
    return (
      <div className="flex justify-center items-center pt-20 w-full">
        <Spinner />
      </div>
    );
  }
  // if (!paginationData) {
  // 	return <div>No data available</div>; // Return something meaningful if no pagination is available
  // }

  return (
    <DataTableComponents
      table={table as ReturnType<typeof useReactTable>}
      columns={columns as ColumnDef<ISocial>[]}
      setPageNo={setPageNo}
      paginationData={paginationData}
      loading={fetching || searchFetching || false}
      sortBy={sortBy}
      setSortBy={(value) => {
        setData([]);
        setSortBy(value);
      }}
      tableType="social"
    />
  );
};
